import { FreeBusyScheduleForResourcesResponse } from "../../../createOrderApi"
import { ResourceFiltersSelectedItem, ResourceFiltersTypes } from "../../../createOrderTypes"
import { TimelineCalendarResource } from "../calendar/fullCalendarTypes"
import { filterResources, isAvailabilityViewFree } from "../helpers"

import { loadServiceCenterFilter } from "./timelineLocationFilterStorage"
import { loadMeetingFitPreference } from "./timelineMeetingFitStorage"

export const SITTINGS_FILTER_IDENTIFIER = "sittings"
export const AVAILABILITY_VIEW_INTERVAL_MINUTES = 5
export const MEETING_WINDOW_DEBOUNCE_MS = 400
// Beyond a day the 5-minute availabilityView string grows unreasonably, and a multi-day booking is the
// book-period flow rather than an Outlook meeting.
export const MAX_AVAILABILITY_WINDOW_HOURS = 24

type InitialFiltersSeed = Readonly<{
    userId: number
    isOutlookFlow: boolean
    guestCount: number
    /** A recurring series is booked as a reserved period, so that mode starts on and stays on. */
    isRecurringBooking?: boolean
}>

/** `guestCount` is already resolved: 0 means the meeting implies no size. */
export const buildSittingsFilter = (guestCount: number): ResourceFiltersSelectedItem[] =>
    guestCount > 0
        ? [
              {
                  identifier: SITTINGS_FILTER_IDENTIFIER,
                  isRemovable: true,
                  values: [{ key: guestCount, value: guestCount }],
              },
          ]
        : []

const buildDefaultResourceTypeFilter = (): ResourceFiltersSelectedItem => ({
    identifier: "resourceTypeIds",
    values: [
        { key: "Room", value: 1 },
        { key: "Av equipment", value: 2 },
    ],
})

// Read synchronously: the chips seed from this state once, on mount.
export const createInitialFilters = ({
    userId,
    isOutlookFlow,
    guestCount,
    isRecurringBooking = false,
}: InitialFiltersSeed): ResourceFiltersTypes => {
    const isMeetingFitOn = isOutlookFlow && loadMeetingFitPreference(userId)

    return {
        filters: [
            buildDefaultResourceTypeFilter(),
            ...(isMeetingFitOn ? buildSittingsFilter(guestCount) : []),
            ...loadServiceCenterFilter(userId),
        ],
        search: "",
        isBookedPeriod: isRecurringBooking,
        onlyAvailableForMeeting: isMeetingFitOn,
    }
}

/** Resources with no schedule entry are "unknown", not busy — a gap in the response must never hide a row. */
export const selectBusyResourceKeys = ({ resources }: FreeBusyScheduleForResourcesResponse): ReadonlySet<string> => {
    const busyKeys = new Set<string>()

    for (const item of resources ?? []) {
        const availability = item.availabilities?.find((slot) => slot.availabilityView)
        if (!availability || isAvailabilityViewFree(availability.availabilityView)) continue

        if (item.resource?.id !== undefined) busyKeys.add(String(item.resource.id))
        if (item.resource?.uid) busyKeys.add(item.resource.uid)
    }

    return busyKeys
}

type MeetingFitCountArgs = Readonly<{
    calendarResources: TimelineCalendarResource[]
    filters: ResourceFiltersSelectedItem[]
    searchRegex: RegExp | undefined
    activeResourceIds: readonly string[] | null
    busyResourceIds: ReadonlySet<string> | undefined
    guestCount: number
}>

export type MeetingFitCounts = Readonly<{
    fitsCount: number
    allCount: number
}>

/** Both sides of the meeting narrowing, whichever of them is on screen. */
export const countMeetingFit = ({
    calendarResources,
    filters,
    searchRegex,
    activeResourceIds,
    busyResourceIds,
    guestCount,
}: MeetingFitCountArgs): MeetingFitCounts => {
    const withoutSeats = filters.filter((f) => f.identifier !== SITTINGS_FILTER_IDENTIFIER)
    const withSeats = [...withoutSeats, ...buildSittingsFilter(guestCount)]

    return {
        fitsCount: filterResources(calendarResources, withSeats, searchRegex, activeResourceIds, busyResourceIds)
            .length,
        allCount: filterResources(calendarResources, withoutSeats, searchRegex, activeResourceIds).length,
    }
}
